/*
 * Saved Templates tab — fetch lifecycle + search/brand-filter state for the
 * list. Mirrors useSlideTemplates.ts's mount/abort discipline (AbortController
 * on cleanup, isMountedRef around setState). Single-select, no insert step —
 * selecting a row extracts its brand via useBrandExtractor's extractFromUrls.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { getErrorMessage, isAbortError } from "../../shared/services/connectorErrors";
import {
  fetchSavedBrandTemplates,
  logSavedBrandTemplatesError,
  type SavedBrandTemplate,
} from "../services/brandTemplatesApi";

const FETCH_FAILED_MESSAGE = "Couldn't load saved templates";

export type SavedBrandTemplatesLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; templates: readonly SavedBrandTemplate[] };

export function useSavedBrandTemplates() {
  const http = useDeckorateHttp();
  const [loadState, setLoadState] = useState<SavedBrandTemplatesLoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoadState({ status: "loading" });
      try {
        const templates = await fetchSavedBrandTemplates(http, signal ? { signal } : {});
        if (!isMountedRef.current) return;
        setLoadState({ status: "ready", templates });
      } catch (error) {
        if (isAbortError(error)) return;
        const message = getErrorMessage(error, FETCH_FAILED_MESSAGE);
        logSavedBrandTemplatesError("saved-brand-templates-fetch", message);
        if (!isMountedRef.current) return;
        setLoadState({ status: "error", message });
      }
    },
    [http],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).catch((error: unknown) =>
      logSavedBrandTemplatesError("saved-brand-templates-fetch-unhandled", getErrorMessage(error, FETCH_FAILED_MESSAGE)),
    );
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(() => {
    load().catch((error: unknown) => logSavedBrandTemplatesError("saved-brand-templates-retry", getErrorMessage(error, FETCH_FAILED_MESSAGE)));
  }, [load]);

  const templates = loadState.status === "ready" ? loadState.templates : [];

  const brandOptions = useMemo(() => {
    const names = new Set<string>();
    for (const template of templates) {
      if (template.brand) names.add(template.brand);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    return templates.filter((template) => {
      if (brandFilter && template.brand !== brandFilter) return false;
      if (!query) return true;
      return template.name.toLowerCase().includes(query) || (template.brand?.toLowerCase().includes(query) ?? false);
    });
  }, [templates, search, brandFilter]);

  return {
    loadState,
    templates,
    filteredTemplates,
    brandOptions,
    search,
    setSearch,
    brandFilter,
    setBrandFilter,
    retry,
  };
}
